import { useState, useCallback, useRef, useEffect } from 'react';

import { AxiosError } from 'axios';

import { Pagination } from '../types';

//                            _
//                         ,:'/   _..._
//                        // ( `""-.._.'
//                        \| /    6\___
//                        |     6      4
//                        |            /
//                        \_       .--'
//                        (_'---'`)
//                        / `'---`()
//                      ,'        |
//      ,            .'`          |
//      )\       _.-'             ;
//     / |    .'`   _            /
//   /` /   .'       '.        , |
//  /  /   /           \   ;   | |
//  |  \  |            |  .|   | |
//   \  `"|           /.-' |   | |
//    '-..-\       _.;.._  |   |.;-.
//          \    <`.._  )) |  .;-. ))
//          (__.  `  ))-'  \_    ))'
//              `'--"`       `"""`

// Hook for fetching data from the API and handling basic pagination.
// Uses:
//   Basic fetch:
//   1. Call the `fetch()` method to load data.
//   2. Use `data`, `loading`, and `error` values to render desired UX.
//   3. If not using pagination, simply ignore the returned `hasMoreData` and `fetchMore` values.

//   Pagination:
//   1. Call `fetch()` when the component inits or a filter/sort changes. (HINT: Should almost always be called from a `useEffect`)
//   2. Use `data`, `loading`, and `error` to render the desired UX.
//   3. Conditionally show a 'load more' button at the end of the list if `!loading && hasMoreData`.
//   4. Call `fetchMore()` when 'load more' button is clicked.

//   Custom fetch:
//   1. `fetch()` can be used to request a particular page and control what happens to the returned data.
//   2. Param options:
//     a. page: number -> Request a particular page of data from the backend.
//     b. resetData: boolean -> Nil out existing data values and replace with newly requested values.
//     c. appendData: boolean -> If existing data is preserved and response value is an array, attempt to append to existing data array.
//   NOTE: `appendData` is only useful when `resetData = false` and working with list values.

type Options<D> = {
  onCompleted?: (data: D) => void;
  onError?: (error: FetchError) => void;
  lazyFetch?: boolean;
  skip?: boolean;
};

export type RequestParams = { page?: number };
export type FetchError = AxiosError;
export type FetchDataFn<P, D> = (params: P) => Promise<[D, Pagination | null]>;
export type FetchOptions = { page?: number, resetData?: boolean, appendData?: boolean };

type ReturnValues<D> = [
  D | undefined,
  boolean,
  FetchError | undefined,
  (options?: FetchOptions) => Promise<D | undefined>,
  boolean,
  () => Promise<D | undefined>,
  Pagination | null,
];

export function useFetch<P extends RequestParams, D>(fetchDataFn: FetchDataFn<P, D>, params: P, options: Options<D> = {}): ReturnValues<D> {
  const [loading, setLoading] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [data, setData] = useState<D>();
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [error, setError] = useState<AxiosError>();

  const isMountedRef = useRef(true);

  const fetchData = async ({ page = 1, resetData = true, appendData = true }: FetchOptions = {}) => {
    try {
      if (options.skip) {
        return data;
      }

      setLoading(true);

      // Start with a new set of data unless specified otherwise.
      // Useful for when filters or sort changes.
      if (resetData) {
        setData(undefined);
      }

      const fetchParams: P = { ...params };
      if (page !== 1) {
        fetchParams.page = page;
      }

      const [resData, resPagination] = await fetchDataFn(fetchParams);

      let result: D = resData;
      if (!resetData && resData && appendData && Array.isArray(data)) {
        result = (data as any).concat(resData);
      }

      if (isMountedRef.current) {
        setData(result);

        if (resPagination) {
          setPagination(resPagination);
          const canLoadMore = resPagination.page < resPagination.total_pages;
          if (canLoadMore !== hasMoreData) {
            setHasMoreData(canLoadMore);
          }
        } else if (hasMoreData) {
          setHasMoreData(false);
        }
        options.onCompleted?.(result);
      }

      return result;
    } catch (error: any) {
      // If there's an error, we're not able to fetch more:
      if (hasMoreData && isMountedRef.current) {
        setHasMoreData(false);
      }

      // If the error didn't come from the backend, don't swallow it.
      if (error.response) {
        if (isMountedRef.current) {
          setError(error);
          options.onError?.(error);
        }
      } else {
        throw error;
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Memoize the handlers so they maintain the same reference between renders:
  const stringifiedParams = JSON.stringify(params);
  const fetch = useCallback(fetchData, [stringifiedParams, hasMoreData, options.skip]);
  const fetchMore = useCallback(() => fetchData({ page: (pagination?.page || 1) + 1, resetData: false }), [stringifiedParams, pagination, hasMoreData, options.skip]);

  useEffect(() => {
    if (!options.lazyFetch && !options.skip) {
      fetch();
    }
  }, [stringifiedParams, options.skip]);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  return [data, loading, error, fetch, hasMoreData, fetchMore, pagination];
}
